import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthApi } from "./ops";
import {
  SignupInput,
  LoginInput,
  LogoutInput,
  UpdateBasicInfoInput,
} from "@/dashboard-api-types/api/auth.types";
import { useUserContext } from "contexts/contextUser";

export const authKeys = {
  all: ["auth"] as const,
  me: () => [...authKeys.all, "me"] as const,
  users: () => [...authKeys.all, "users"] as const,
};

export function useSignupAdmin() {
  const queryClient = useQueryClient();
  const { accessToken } = useUserContext();
  return useMutation({
    mutationFn: (data: SignupInput) => AuthApi.signupAdmin(data, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.users() });
    },
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (data: LoginInput) => AuthApi.login(data),
  });
}

export function useRefreshSession() {
  return useMutation({
    mutationFn: () => AuthApi.refreshSession(),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LogoutInput) => AuthApi.logout(),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: authKeys.me() });
    },
  });
}

export function useMe() {
  const { accessToken } = useUserContext();
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: ({ signal }: { signal?: AbortSignal }) => AuthApi.me(accessToken, signal),
    enabled: !!accessToken, // Only fetch if access token exists
  });
}

export function useListUsers() {
  const { accessToken } = useUserContext();
  return useQuery({
    queryKey: authKeys.users(),
    queryFn: ({ signal }: { signal?: AbortSignal }) => AuthApi.listUsers(accessToken, signal),
    enabled: !!accessToken, // Only fetch if access token exists
  });
}

export function useUpdatePassword() {
  const { accessToken } = useUserContext();
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      AuthApi.updatePassword(userId, newPassword, accessToken),
  });
}

export function useUpdateBasicInfo() {
  const { accessToken } = useUserContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateBasicInfoInput }) =>
      AuthApi.updateBasicInfo(userId, data, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      queryClient.invalidateQueries({ queryKey: authKeys.users() });
    },
  });
}

export function useDeleteUser() {
  const { accessToken } = useUserContext();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => AuthApi.deleteUser(userId, accessToken),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.users() });
    },
  });
}
